import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { createPortal } from 'react-dom';
import { FocusOn } from 'react-focus-on';
import { useId } from 'react-id-generator';
import styled from 'styled-components';
import { FUTURE_TOKENS } from '../../constants/futureTokens.js';
import { spacing4x, spacing10x, spacing1x } from '../../styles/spacing.js';
import { useThemeContext } from '../../styles/themes/themes.js';
import { Backdrop } from '../Backdrop/Backdrop.js';
import { Box } from '../Box/Box.js';
import { Button } from '../Button/Button.js';
import { IconClose } from '../Icons/IconClose.js';
import { MODAL_DATA_TESTIDS } from './constants.js';
import { jsx, Fragment, jsxs } from 'react/jsx-runtime';

var _excluded = ["autoFocus", "children", "className", "closeOnOutsideClick", "containerSelector", "displayCloseButton", "focusTrapProps", "footer", "footerButtonProps", "fullscreen", "header", "inverted", "maxModalHeight", "modalAriaLabel", "onClose", "onEscKeyDown", "onOutsideClick", "open", "preserveBodyHeight", "size"];
var ModalWidth;
(function (ModalWidth2) {
  ModalWidth2[ModalWidth2["xxs"] = 320] = "xxs";
  ModalWidth2[ModalWidth2["xs"] = 400] = "xs";
  ModalWidth2[ModalWidth2["sm"] = 600] = "sm";
  ModalWidth2[ModalWidth2["md"] = 800] = "md";
  ModalWidth2[ModalWidth2["lg"] = 1040] = "lg";
  ModalWidth2[ModalWidth2["xl"] = 1400] = "xl";
})(ModalWidth || (ModalWidth = {}));
var ModalContent = styled(Box).withConfig({
  displayName: "Modal__ModalContent",
  componentId: "sc-1dinmru-0"
})(["&&{background-color:", ";display:flex;flex-direction:column;outline:none;position:relative;", ";}&& ._mntl-modal-header{align-items:center;border-bottom:", ";border-start-start-radius:inherit;border-start-end-radius:inherit;display:flex;min-height:60px;padding:0 ", ";background-color:", ";color:", ";}&& ._mntl-modal-header ._mntl-modal-header-content{flex-grow:1;align-items:center;column-gap:8px;display:flex;}&& ._mntl-modal-header ._mntl-modal-header-content > *{align-items:center;display:flex;}&& ._mntl-modal-header ._mntl-modal-header-content:has(.mntl-modal-header-end){justify-content:space-between;}&& ._mntl-modal-header ._mntl-modal-header-content .mntl-modal-header-start{align-items:start;display:flex;flex-grow:1;}&& ._mntl-modal-header ._mntl-modal-header-content .mntl-modal-header-start > *{line-height:1;}&& ._mntl-modal-header ._mntl-modal-header-content .mntl-modal-header-start:has(.mntl-modal-header-start-subtitle){flex-direction:column;row-gap:4px;}&& ._mntl-modal-header ._mntl-modal-header-content .mntl-modal-header-start:has(.mntl-modal-header-start-subtitle) > *{white-space:nowrap;}&& ._mntl-modal-header ._mntl-modal-header-content .mntl-modal-header-start-subtitle,&& ._mntl-modal-header ._mntl-modal-header-content .mntl-modal-header-mid,&& ._mntl-modal-header ._mntl-modal-header-content .mntl-modal-header-end{column-gap:8px;display:flex;font-size:0.9em;line-height:1;}&& ._mntl-modal-header ._mntl-modal-header-content .mntl-modal-header-end{padding-inline-start:4px;}&& ._mntl-modal-header ._mntl-modal-header-content .mntl-modal-header-mid{align-self:stretch;border-color:transparent;border-inline-end-color:", ";border-style:solid;border-width:1px;padding-inline-end:24px;}"], function (_ref) {
  var inverted = _ref.inverted,
    theme = _ref.theme;
  if (inverted) {
    return theme.neutralColors.pearl;
  }
  return theme.neutralColors.white;
}, function (_ref2) {
  var fullscreen = _ref2.fullscreen,
    maxHeight = _ref2.maxHeight,
    width = _ref2.width;
  return "\n            border-width: ".concat(fullscreen ? 0 : "1px", ";\n            height: ").concat(fullscreen ? "100vh" : "auto", ";\n            max-height: ").concat(fullscreen ? "none" : maxHeight, ";\n            max-width: ").concat(fullscreen ? "none" : "calc(100vw - 64px)", ";\n            width: ").concat(fullscreen ? "100vw" : "".concat(width, "px"), ";\n        ");
}, function (_ref3) {
  var fullscreen = _ref3.fullscreen,
    theme = _ref3.theme,
    width = _ref3.width;
  if (width !== 320 || width === 320 && fullscreen) {
    return "1px solid ".concat(theme.neutralColors.silver);
  }
}, spacing4x, function (_ref4) {
  var inverted = _ref4.inverted,
    theme = _ref4.theme;
  if (inverted) {
    return theme.neutralColors.charcoal;
  }
}, function (_ref5) {
  var inverted = _ref5.inverted,
    theme = _ref5.theme;
  if (inverted) {
    return theme.neutralColors.white;
  }
}, function (_ref6) {
  var theme = _ref6.theme;
  return theme.neutralColors.rhino;
});
var ContentWrapper = styled.div.withConfig({
  displayName: "Modal__ContentWrapper",
  componentId: "sc-1dinmru-1"
})(["&&{display:flex;flex-direction:column;flex-grow:1;overflow:auto;padding:", ";}"], spacing4x);
var CloseButtonSection = styled.section.withConfig({
  displayName: "Modal__CloseButtonSection",
  componentId: "sc-1dinmru-2"
})(["&&{display:flex;flex-grow:0;flex-shrink:0;flex-basis:", ";justify-content:flex-end;padding-block-start:", ";}"], spacing10x, spacing1x);
var FooterWrapper = styled.div.withConfig({
  displayName: "Modal__FooterWrapper",
  componentId: "sc-1dinmru-3"
})(["&&{padding:", ";border-top:1px solid ", ";}"], spacing4x, function (_ref7) {
  var theme = _ref7.theme;
  return theme.neutralColors.silver;
});
var FooterButtonsContainer = styled.div.withConfig({
  displayName: "Modal__FooterButtonsContainer",
  componentId: "sc-1dinmru-4"
})(["&&{display:flex;justify-content:flex-end;}"]);
var Modal = function Modal(props) {
  var autoFocus = props.autoFocus,
    children = props.children,
    className = props.className,
    _props$closeOnOutside = props.closeOnOutsideClick,
    closeOnOutsideClick = _props$closeOnOutside === void 0 ? false : _props$closeOnOutside,
    containerSelector = props.containerSelector,
    _props$displayCloseBu = props.displayCloseButton,
    displayCloseButton = _props$displayCloseBu === void 0 ? true : _props$displayCloseBu,
    focusTrapProps = props.focusTrapProps,
    footer = props.footer,
    footerButtonProps = props.footerButtonProps,
    _props$fullscreen = props.fullscreen,
    fullscreen = _props$fullscreen === void 0 ? false : _props$fullscreen,
    header = props.header,
    _props$inverted = props.inverted,
    inverted = _props$inverted === void 0 ? false : _props$inverted,
    maxModalHeight = props.maxModalHeight,
    modalAriaLabel = props.modalAriaLabel,
    onClose = props.onClose,
    onEscKeyDown = props.onEscKeyDown,
    onOutsideClick = props.onOutsideClick,
    _props$open = props.open,
    open = _props$open === void 0 ? true : _props$open,
    _props$preserveBodyHe = props.preserveBodyHeight,
    preserveBodyHeight = _props$preserveBodyHe === void 0 ? false : _props$preserveBodyHe,
    _props$size = props.size,
    size = _props$size === void 0 ? "sm" : _props$size,
    rest = _objectWithoutProperties(props, _excluded);
  var theme = useThemeContext();
  var _useId = useId(1, "modal-"),
    _useId2 = _slicedToArray(_useId, 1),
    titleId = _useId2[0];
  var width = ModalWidth[size] || 400;
  var _onEscapeKey = onEscKeyDown || onClose;
  var _onClickOutside = onOutsideClick || (closeOnOutsideClick ? onClose : void 0);
  var footerSection;
  var headerSection;
  if (footer) {
    footerSection = footer;
  } else if (footerButtonProps !== null && footerButtonProps !== void 0 && footerButtonProps.length) {
    footerSection = /*#__PURE__*/jsx(FooterButtonsContainer, {
      children: footerButtonProps.map(function (btnProps, i) {
        return /*#__PURE__*/jsx(Button, _objectSpread({
          style: {
            marginInlineStart: i === 0 ? 0 : spacing4x
          }
        }, btnProps), i);
      })
    });
  }
  if (header) {
    headerSection = typeof header === "function" ? header(titleId) : /*#__PURE__*/jsx("h2", {
      className: "h4",
      "aria-level": 1,
      id: titleId,
      children: header
    });
  }
  function getPortalEl() {
    var selector = containerSelector || theme.defaultPortalContainerSelector;
    if (!selector) return document.body;
    return document.querySelector(selector) || document.body;
  }
  return open ? /*#__PURE__*/jsx(Fragment, {
    children: /*#__PURE__*/createPortal( /*#__PURE__*/jsx(Backdrop, {
      fullscreen: true,
      style: {
        overflow: "auto",
        background: FUTURE_TOKENS.MODAL_FULLSCREEN_BACKGROUND_COLOR
      },
      open: true,
      children: /*#__PURE__*/jsx(FocusOn, {
        autoFocus: autoFocus,
        shards: focusTrapProps === null || focusTrapProps === void 0 ? void 0 : focusTrapProps.included,
        shouldIgnore: focusTrapProps === null || focusTrapProps === void 0 ? void 0 : focusTrapProps.shouldInclude,
        onClickOutside: _onClickOutside,
        onEscapeKey: _onEscapeKey,
        onActivation: function onActivation() {
          if (preserveBodyHeight) {
            document.body.style.setProperty("position", "unset", "important");
          }
        },
        onDeactivation: function onDeactivation() {
          if (preserveBodyHeight) {
            document.body.style.removeProperty("position");
          }
        },
        children: /*#__PURE__*/jsxs(ModalContent, _objectSpread(_objectSpread({
          "aria-label": modalAriaLabel,
          "aria-labelledby": modalAriaLabel ? void 0 : titleId,
          "aria-modal": "true",
          borderRadius: fullscreen ? 0 : 2,
          className: className,
          "data-testid": MODAL_DATA_TESTIDS.content,
          elevation: fullscreen ? void 0 : 3,
          fullscreen: fullscreen,
          inverted: inverted,
          role: "dialog",
          maxHeight: maxModalHeight ? "".concat(maxModalHeight, "px") : "calc(100vh - 64px)",
          width: width,
          tabIndex: 0
        }, rest), {}, {
          children: [headerSection && /*#__PURE__*/jsxs("header", {
            className: "_mntl-modal-header",
            children: [/*#__PURE__*/jsx("section", {
              className: "_mntl-modal-header-content",
              children: headerSection
            }), displayCloseButton && /*#__PURE__*/jsx(CloseButtonSection, {
              children: /*#__PURE__*/jsx(Button, {
                label: "Close",
                variant: inverted ? "invertedGhost" : "ghost",
                hideLabel: true,
                iconSize: 16,
                onClick: onClose,
                startAdornment: IconClose
              })
            })]
          }), typeof children === "function" ? /*#__PURE__*/jsx(ContentWrapper, {
            style: {
              padding: 0
            },
            children: children()
          }) : /*#__PURE__*/jsxs(ContentWrapper, {
            style: {
              display: displayCloseButton && !headerSection ? "flex" : void 0
            },
            children: [children, displayCloseButton && !headerSection && /*#__PURE__*/jsx(CloseButtonSection, {
              children: /*#__PURE__*/jsx(Button, {
                label: "Close",
                variant: inverted ? "invertedGhost" : "ghost",
                hideLabel: true,
                iconSize: 16,
                onClick: onClose,
                startAdornment: IconClose
              })
            })]
          }), footerSection && /*#__PURE__*/jsx(FooterWrapper, {
            children: footerSection
          })]
        }))
      })
    }), getPortalEl())
  }) : null;
};
Modal.displayName = "Modal";

export { Modal };
